import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper,
    Checkbox, FormControlLabel, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

// IHHP is modular (same as IHHP_Rates): Hospital Plan is the required base, the rest are
// independently selectable, except Module 4A and 4B which are mutually exclusive.
const MODULES = [
    { key: 'hp',  rateKey: 'hospital_plan',                   label: 'HP',  fullLabel: 'Hospital Plan',                          premiumField: 'hp_premium' },
    { key: 'm1',  rateKey: 'non_hospitalisation_benefits',    label: 'M1',  fullLabel: 'Module 1 (Non-Hospitalisation Benefits)', premiumField: 'm1_premium' },
    { key: 'm2',  rateKey: 'medicine_appliances',             label: 'M2',  fullLabel: 'Module 2 (Medicine & Appliances)',        premiumField: 'm2_premium' },
    { key: 'm3',  rateKey: 'medical_evacuation_repatriation', label: 'M3',  fullLabel: 'Module 3 (Medical Evacuation)',           premiumField: 'm3_premium' },
    { key: 'm4a', rateKey: 'dental_optical_a',                label: 'M4A', fullLabel: 'Module 4A (Dental & Optical)',            premiumField: 'm4a_premium' },
    { key: 'm4b', rateKey: 'dental_optical_b',                label: 'M4B', fullLabel: 'Module 4B (Enhanced Dental & Optical)',   premiumField: 'm4b_premium' },
];

// Plans saved before the checkbox selection used fixed cumulative tiers
const LEGACY_PLAN_MODULES = {
    ihhp_hp:  ['hp'],
    ihhp_m1:  ['hp', 'm1'],
    ihhp_m2:  ['hp', 'm1', 'm2'],
    ihhp_m4a: ['hp', 'm1', 'm2', 'm4a'],
    ihhp_m4b: ['hp', 'm1', 'm2', 'm4b'],
};

const DEFAULT_SELECTED_MODULES = { hp: true, m1: false, m2: false, m3: false, m4a: false, m4b: false };

function getModulesFromPlanId(planId) {
    if (LEGACY_PLAN_MODULES[planId]) return LEGACY_PLAN_MODULES[planId];
    return (planId || '').replace(/^ihhp_/, '').split('_').filter(Boolean);
}

const CURRENCIES = ['USD', 'EURO', 'GBP'];
const PAYMENT_FREQUENCIES = ['Annually', 'Semi Annually', 'Quarterly', 'Monthly'];

function getCurrencySign(currency) {
    if (currency === 'USD') return '$';
    if (currency === 'SGD') return 'S$';
    if (currency === 'EURO') return '€';
    if (currency === 'GBP') return '£';
    return '';
}

function formatRate(rate, currency) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return getCurrencySign(currency) + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Same as IHHP_Rates: discount/loading scale every module rate
function getRateFactor(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

export default function IHHP_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const areaOptions = appConfig?.content?.ihhp_area_of_coverage || [];
    const defaultArea = areaOptions.includes('REST OF THE WORLD (Excl. Hong Kong & U.S. & European Economic Area)')
        ? 'REST OF THE WORLD (Excl. Hong Kong & U.S. & European Economic Area)'
        : (areaOptions[0] || '');
    const getDeductibleOptions = (curr) => appConfig?.content?.deductibles?.ihhp?.[curr] || [];
    // Age loadings — already applied to HP/M1/M2 by the backend, shown in the client header like IHHP_Rates
    const ageLoadings = appConfig?.content?.specifics?.ihhp?.loadings || {};

    const [selectedModules, setSelectedModules] = useState(DEFAULT_SELECTED_MODULES);
    const [areaOfCoverage, setAreaOfCoverage] = useState(defaultArea);
    const [currency, setCurrency] = useState('USD');
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    // { [quoteName]: string }
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    // { [quoteName]: true }
    const [deductibleErrors, setDeductibleErrors] = useState({});
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const initializingFromEdit = useRef(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const activeModules = MODULES.filter(m => selectedModules[m.key]);
    const selectedPlanKey = activeModules.map(m => m.key).join('_');
    const selectedPlanId = `ihhp_${selectedPlanKey}`;
    const selectedPlanLabel = activeModules.map(m => m.label).join(' + ');

    // Pre-populate form when opening in edit mode
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            initializingFromEdit.current = true;
            const editingKeys = getModulesFromPlanId(editingPlan.plan_id);
            setSelectedModules(Object.fromEntries(MODULES.map(m => [m.key, m.key === 'hp' || editingKeys.includes(m.key)])));
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || defaultArea);
            setCurrency(editingPlan.payloads?.currency || 'USD');
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            const savedDiscount = editingPlan.payloads?.discount ?? '0';
            const savedLoading = editingPlan.payloads?.loading ?? '0';
            setDiscount(String(savedDiscount));
            setLoadingPercent(String(savedLoading));
            const savedFactor = getRateFactor(savedDiscount, savedLoading);
            // Undo the saved discount/loading so ratesData holds the raw rate again
            const unscale = val => (val != null && savedFactor > 0 ? val / savedFactor : val);
            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid] || '';
            });
            setQuoteDeductibles(deductibles);

            // Reconstruct ratesData from stored per-module premiums
            const ratesMap = {};
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                // Match the specific plan instance (plan_uid) so editing one of several same-tier
                // plans doesn't pull rates from a sibling; fall back to plan_id for plans saved
                // before per-instance uids existed.
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(p =>
                        editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id
                    );
                    return Object.fromEntries(MODULES.map(m => [m.rateKey, unscale(premium?.[m.premiumField] ?? null)]));
                });
                ratesMap[quote.uid] = { rawRates };
            });
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
            // Clear the init flag AFTER all dependent effects have fired
            setTimeout(() => { initializingFromEdit.current = false; }, 0);
        } else if (open) {
            setSelectedModules(DEFAULT_SELECTED_MODULES);
            setDiscount('0');
            setLoadingPercent('0');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Reset deductibles to defaults when currency changes (skip during edit init)
    useEffect(() => {
        if (initializingFromEdit.current) return;
        const defaults = {};
        (quotes || []).forEach(q => {
            defaults[q.name] = getDeductibleOptions(currency)[0] || '';
        });
        setQuoteDeductibles(defaults);
        invalidateRates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency]);

    function handleModuleChange(moduleKey, checked) {
        setSelectedModules(prev => {
            const next = { ...prev, [moduleKey]: checked };
            // Module 4A and 4B are mutually exclusive
            if (checked && moduleKey === 'm4a') next.m4b = false;
            if (checked && moduleKey === 'm4b') next.m4a = false;
            return next;
        });
        // Rates are kept — the API returns every module's rate in one fetch, so the table just
        // shows a different set of rows.
    }

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name] === LEAVE_BLANK;
    }

    async function handleViewRates() {
        // Validate: each quote must have a deductible selected
        const errors = {};
        (quotes || []).forEach(q => {
            if (!quoteDeductibles[q.name]) errors[q.name] = true;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const firstQuote = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please select a deductible for "${firstQuote?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: {
                                ihhp: quoteDeductibles[quote.name] || '',
                            },
                            insurers: ['ihhp'],
                            specifics: {},
                            specific_area_of_coverage: { ihhp: areaOfCoverage },
                            rates_version_per_insurer: { ihhp: selectedRatesVersion?.ihhp || '' },
                            specific_currency: { ihhp: currency },
                            specific_payment_frequency: { ihhp: paymentFrequency },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.ihhp }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map(r => Object.fromEntries(MODULES.map(m => [m.rateKey, r[m.rateKey]])));
                    ratesMap[quoteUid] = { rawRates };
                } else {
                    ratesMap[quoteUid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching IHHP rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    const rateFactor = getRateFactor(discount, loadingPercent);

    // Plan total = sum of active module rates for a given client.
    // ratesData keeps the raw API rates; discount/loading are applied on read.
    function getRate(clientIndex, quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        let total = 0;
        let hasAny = false;
        for (const m of activeModules) {
            const r = getRawRate(clientIndex, m.rateKey, quoteUid);
            if (typeof r === 'number') { total += r; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function getSubtotal(quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getRate(i, quoteUid);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    // Individual module rate with discount/loading applied
    function getRawRate(clientIndex, rateKey, quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        const rate = ratesData[quoteUid].rawRates[clientIndex]?.[rateKey];
        return typeof rate === 'number' ? rate * rateFactor : rate;
    }

    function getRawSubtotal(rateKey, quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getRawRate(i, rateKey, quoteUid);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function getAgeLoading(age) {
        for (const range in ageLoadings) {
            const [min, max] = range.split('-').map(Number);
            if (age >= min && age <= max) return ageLoadings[range];
        }
        return 0;
    }

    function displayAgeLoading(age) {
        const val = getAgeLoading(age);
        if (!val) return '';
        return `(${val * 100}% loadings)`;
    }

    function buildIhhpDeductibleString(quoteName) {
        const val = quoteDeductibles[quoteName] || '';
        if (!val) return '';
        const isZero = val.toLowerCase().includes('zero') || val.toLowerCase().includes('nil');
        if (isZero) return 'Zero Deductible';
        return `${val}`;
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const activeModuleKeys = activeModules.map(m => m.key);
        // plan_id identifies the module combination and is shared by every instance of it; uid
        // identifies this specific plan instance so multiple plans of the same combination can
        // coexist instead of overwriting each other. Editing reuses the existing plan's uid.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const rate = getRate(clientIndex, quote.uid);
                // Only persist the premium for modules the user actually selected — the API
                // returns a rate for every module regardless of selection.
                // Stored with discount/loading applied, matching premium_rate.
                const modulePremiums = Object.fromEntries(MODULES.map(m => [
                    m.premiumField,
                    activeModuleKeys.includes(m.key) ? (getRawRate(clientIndex, m.rateKey, quote.uid) ?? null) : null,
                ]));
                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: selectedPlanId,
                    planUid,
                    premium: {
                        insurer: 'ihhp',
                        plan: selectedPlanKey,
                        plan_label: selectedPlanLabel,
                        included_benefits: '',
                        deductible: buildIhhpDeductibleString(quote.name),
                        area_of_coverage: areaOfCoverage,
                        currency,
                        payment_frequency: paymentFrequency,
                        discount,
                        loading: loadingPercent,
                        premium_rate: rate ?? null,
                        ...modulePremiums,
                    },
                });
            });
        });

        const plan = {
            plan_id: selectedPlanId,
            uid: planUid,
            plan: selectedPlanKey,
            insurer: 'ihhp',
            label: `BUPA IHHP KSK ${selectedPlanLabel}`,
            payloads: {
                area_of_coverage: areaOfCoverage,
                currency,
                payment_frequency: paymentFrequency,
                discount,
                loading: loadingPercent,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || ''])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#b3a66e', px: 2, py: 1 }}>
                <div style={{
                    color: '#fff',
                    backgroundColor: '#98884a',
                    width: 100,
                    height: 60,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 13,
                    lineHeight: 1.2,
                }}>International Health and Hospital plan</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img src={`${process.env.PUBLIC_URL}/images/KSK Logo.png`} alt="KSK Insurance" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <img src={`${process.env.PUBLIC_URL}/images/Bupa Yellow Logo.png`} alt="BUPA" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                {/* Select Plan — modular: Hospital Plan is the required base, the rest are independently selectable */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                        {MODULES.map(module => (
                            <FormControlLabel
                                key={module.key}
                                sx={{ mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={!!selectedModules[module.key]}
                                        disabled={loading || !!editingPlan}
                                        onChange={(e) => {
                                            // Hospital Plan is the required base module — read-only, always checked
                                            if (module.key === 'hp') return;
                                            handleModuleChange(module.key, e.target.checked);
                                        }}
                                    />
                                }
                                label={module.fullLabel}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Options — label on the left, select on the right */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 190px', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Area Of Coverage</Typography>
                        <FormControl size="small" sx={{ width: 190 }}>
                            <Select
                                value={areaOfCoverage}
                                disabled={loading}
                                onChange={(e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }}
                                sx={{ fontSize: 12, height: 32 }}
                            >
                                {areaOptions.map(area => (
                                    <MenuItem key={area} value={area} sx={{ fontSize: 12 }}>{area}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Currency</Typography>
                        <FormControl size="small" sx={{ width: 190 }}>
                            <Select
                                value={currency}
                                disabled={loading}
                                onChange={(e) => setCurrency(e.target.value)}
                                sx={{ fontSize: 12, height: 32 }}
                            >
                                {CURRENCIES.map(c => (
                                    <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 190 }}>
                            <Select
                                value={paymentFrequency}
                                disabled={loading}
                                onChange={(e) => { setPaymentFrequency(e.target.value); invalidateRates(); }}
                                sx={{ fontSize: 12, height: 32 }}
                            >
                                {PAYMENT_FREQUENCIES.map(f => (
                                    <MenuItem key={f} value={f} sx={{ fontSize: 12 }}>{f}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Per-Quote Deductibles — single deductible per quote */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductibles per Quote</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 200 }}>{quote.name}</Typography>
                                <CustomSelect2
                                    dense
                                    value={quoteDeductibles[quote.name] || ''}
                                    onChange={(e) => {
                                        setQuoteDeductibles(prev => ({ ...prev, [quote.name]: e.target.value }));
                                        setDeductibleErrors(prev => ({ ...prev, [quote.name]: false }));
                                        invalidateRates();
                                    }}
                                    items={[LEAVE_BLANK, ...getDeductibleOptions(currency)]}
                                    disabled={loading}
                                    error={!!deductibleErrors[quote.name]}
                                    sx={{ minWidth: 200 }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {/* Rates Display */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: '#b3a66e' }} />
                    </Box>
                )}

                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ mb: 3 }}>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                    {quote.name}
                                    <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                        — {quoteDeductibles[quote.name] || '-'}
                                    </Typography>
                                </Typography>
                                {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#b3a66e' }}>
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 260 }}>Plan</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                        <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                        <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                        <div style={{ fontWeight: 400, fontSize: 12 }}>{displayAgeLoading(client.age)}</div>
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* One row per active module showing individual rate */}
                                            {activeModules.map((module, moduleIdx) => (
                                                <TableRow key={module.key} sx={{ backgroundColor: moduleIdx % 2 === 0 ? '#f5f7fa' : '#fff' }}>
                                                    <TableCell sx={{ color: 'text.secondary' }}>{module.fullLabel}</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            {formatRate(getRawRate(idx, module.rateKey, quote.uid), currency)}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {formatRate(getRawSubtotal(module.rateKey, quote.uid), currency)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {/* Plan total row — only shown when multiple modules */}
                                            {activeModules.length > 1 && (
                                                <TableRow sx={{ backgroundColor: '#f3efdf' }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>{selectedPlanLabel}</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx} sx={{ fontWeight: 700 }}>
                                                            {formatRate(getRate(idx, quote.uid), currency)}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 700 }}>
                                                        {formatRate(getSubtotal(quote.uid), currency)}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Paper>
)}
                            </Box>
                        ))}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId="ihhp" />

                <RatesVersionSelect
                    insurer="ihhp"
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
